import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { consumeFlashToast, FLASH_TOAST_EVENT, type FlashToast } from '../../utils/flashToast';

function messageFromQuery(search: string): string | null {
  const v = new URLSearchParams(search).get('toast');
  if (v === 'created') return 'Role created successfully.';
  if (v === 'updated') return 'Role updated successfully.';
  return null;
}

export function ToastHost() {
  const location = useLocation();
  const navigate = useNavigate();
  const [toast, setToast] = useState<FlashToast | null>(null);
  const timerRef = useRef<number | null>(null);

  function show(next: FlashToast) {
    setToast(next);
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setToast(null), 5200);
  }

  // Only clear timers on unmount (not on every navigation),
  // otherwise a replace-navigation (URL cleanup) cancels auto-dismiss.
  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    const onFlash = (e: Event) => {
      const detail = (e as CustomEvent<FlashToast>).detail;
      if (detail?.message) show(detail);
    };
    window.addEventListener(FLASH_TOAST_EVENT, onFlash as EventListener);
    return () => window.removeEventListener(FLASH_TOAST_EVENT, onFlash as EventListener);
  }, []);

  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('[ToastHost] navigation', {
        pathname: location.pathname,
        search: location.search,
        state: location.state,
        key: location.key,
      });
    }

    const navToastRaw = (location.state as { toast?: unknown } | null | undefined)?.toast;
    const navToast =
      typeof navToastRaw === 'string'
        ? ({ message: navToastRaw, variant: 'success' } satisfies FlashToast)
        : navToastRaw && typeof navToastRaw === 'object' && 'message' in (navToastRaw as Record<string, unknown>)
          ? (navToastRaw as FlashToast)
          : null;
    const qsToast = messageFromQuery(location.search);
    const flash = consumeFlashToast();

    if (import.meta.env.DEV) console.log('[ToastHost] candidates', { navToast, qsToast, flash });

    // If we used ?toast=..., remove it so refresh doesn't re-show.
    // Important: do the cleanup navigation first, passing the toast via navigation state,
    // so we don't lose the toast due to same-tick navigation before paint.
    if (qsToast) {
      if (import.meta.env.DEV) console.log('[ToastHost] consuming query toast and cleaning URL (and re-emitting via state)');
      const sp = new URLSearchParams(location.search);
      sp.delete('toast');
      const nextSearch = sp.toString();
      navigate(
        { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' },
        { replace: true, state: { toast: { message: qsToast, variant: 'success' } satisfies FlashToast } }
      );
      return;
    }

    if (navToast) show(navToast);
    else if (flash?.message && flash.variant !== 'error') show(flash);
  }, [location.key]);

  if (!toast) return null;

  const variant = toast.variant ?? 'info';
  const accent =
    variant === 'success'
      ? 'var(--green)'
      : variant === 'error'
        ? 'var(--red)'
        : variant === 'warning'
          ? 'var(--orange)'
          : variant === 'info'
            ? 'var(--blue)'
            : 'var(--t2)';

  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        top: 72,
        right: 18,
        zIndex: 2000,
        maxWidth: 460,
        padding: '10px 12px',
        borderRadius: 'var(--rs)',
        border: `1px solid color-mix(in srgb, ${accent} 28%, var(--border))`,
        background: `color-mix(in srgb, ${accent} 10%, var(--surface))`,
        fontSize: 13,
        color: 'var(--text)',
        boxShadow: '0 12px 30px rgba(0,0,0,0.12)',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <div style={{ width: 8, height: 8, borderRadius: 99, background: accent, flexShrink: 0 }} aria-hidden="true" />
      <div style={{ flex: 1, fontWeight: 700, lineHeight: 1.35 }}>{toast.message}</div>
      <button
        type="button"
        onClick={() => setToast(null)}
        aria-label="Dismiss notification"
        style={{
          border: '1px solid var(--border)',
          background: 'color-mix(in srgb, var(--bg) 45%, var(--surface))',
          color: 'var(--t2)',
          width: 28,
          height: 28,
          borderRadius: 8,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M18 6L6 18" />
          <path d="M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}